import { FieldValues, useForm, UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { FormHTMLAttributes, ReactNode, useEffect } from 'react';

import { schema } from './schema';
import useTranslate from '@src/i18n/useTranslate';
import usePhoneVerification from '@src/hooks/usePhoneVerification';
import useAppEnvironment from '@src/hooks/useAppEnvironment';
import { convertGeneralContextErrorToHookForm } from '@src/common';

export interface RenderProps {
  form: UseFormReturn<FieldValues, any, undefined>;
}

interface Props extends Omit<FormHTMLAttributes<HTMLFormElement>, 'children' | 'onSubmit'> {
  onSuccess?: () => void;
  onFail?: () => void;
  render: (props: RenderProps) => ReactNode;
}

const Form = (props: Props) => {
  const { onFail, onSuccess, render, ...rest } = props;

  const { i18n } = useTranslate();
  const appEnv = useAppEnvironment();
  const { sendCode, status, submit, contextErrors } = usePhoneVerification();

  const form = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
    resolver: zodResolver(schema(i18n)),
  });

  const { errors } = form.formState;

  useEffect(() => {
    if (appEnv === 'production') {
      sendCode();
    }
  }, []);

  useEffect(() => {
    switch (status) {
      case 'success':
        onSuccess?.();
        break;
      case 'error':
        onFail?.();
        break;

      default:
        break;
    }
  }, [status]);

  useEffect(() => {
    convertGeneralContextErrorToHookForm(
      'code',
      form.setError,
      contextErrors,
      'phone_verification',
      !!errors['code'],
      form.clearErrors,
    );
  }, [contextErrors]);

  return (
    <form {...rest} onSubmit={form.handleSubmit((payload) => submit(payload['code']))}>
      {render({ form })}
    </form>
  );
};

export default Form;
